import React, { useEffect, useState, useContext } from 'react';
import { Link, useLocation } from "react-router-dom";
import { Line } from "react-chartjs-2";
import "chart.js/auto";
import "chartjs-adapter-moment";
import moment from 'moment';
import OfficeLocationController from './OfficeLocationController.js';
import OfficeHeaderNav from './OfficeHeaderNav.js';
import OverviewBarChart from './OverviewBarChart.js';
import { getRange, rollupSeries } from './overviewRollup.js';
import Spinner from "../../components/atoms/Spinner.js";
import { ThemeContext } from '../../components/molecules/Nav';

const ENTRANCE_COLOR = '#f59e0b'; // amber
const EXIT_COLOR = '#3b82f6';     // blue

// ---------------------------------------------------------------------------
// Reusable dual-line chart (daily view: continuous hourly time-series)
// ---------------------------------------------------------------------------
function FlowChart({ series, theme, height = 300 }) {
  const txt = theme === 'light' ? '#333' : '#e5e5e5';

  const points = (series || [])
    .filter(p => p.timestamp)
    .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp))
    .map(p => ({ x: new Date(p.timestamp), inV: p.count || 0, outV: p.countOut || 0 }));

  const data = {
    datasets: [
      {
        label: 'Entrances',
        data: points.map(p => ({ x: p.x, y: p.inV })),
        borderColor: ENTRANCE_COLOR, backgroundColor: ENTRANCE_COLOR,
        fill: false, pointRadius: 2, borderWidth: 3, tension: 0.4,
      },
      {
        label: 'Exits',
        data: points.map(p => ({ x: p.x, y: p.outV })),
        borderColor: EXIT_COLOR, backgroundColor: EXIT_COLOR,
        fill: false, pointRadius: 2, borderWidth: 3, tension: 0.4,
      },
    ],
  };

  const maxV = Math.max(...data.datasets.flatMap(d => d.data.map(p => p.y)), 0) || 100;

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        type: 'time',
        time: { unit: 'hour', displayFormats: { hour: 'h A' }, tooltipFormat: 'h:mm A', parser: false },
        grid: { display: false },
        ticks: {
          color: txt, autoSkip: true, maxRotation: 0, minRotation: 0, source: 'auto',
          callback: (v) => new Date(v).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true }),
        },
        title: { display: true, text: 'Time', color: txt },
      },
      y: {
        beginAtZero: true,
        grid: { display: true, color: theme === 'light' ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.08)' },
        ticks: { color: txt, precision: 0 },
        title: { display: true, text: 'People', color: txt },
        max: maxV < 100 ? Math.ceil(maxV / 20) * 20 + 20 : Math.ceil(maxV / 100) * 100 + 100,
      },
    },
    plugins: {
      legend: { labels: { color: txt, usePointStyle: true, pointStyle: 'rectRounded' } },
      tooltip: {
        mode: 'nearest', intersect: false, animation: { duration: 0 },
        callbacks: {
          title: (c) => c.length ? c[0].raw.x.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true }) : '',
          label: (c) => ` ${c.dataset.label}: ${c.raw.y} people`,
        },
      },
    },
  };

  if (!points.length) {
    return (
      <div style={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#888' }}>
        No data for the selected day
      </div>
    );
  }
  return <div style={{ height }}><Line data={data} options={options} /></div>;
}

// ---------------------------------------------------------------------------
// Overview stat tile
// ---------------------------------------------------------------------------
function StatTile({ label, value, color, theme, fullBorder = false }) {
  return (
    <div
      className="panel-2"
      style={{
        flex: '1 1 160px', minWidth: 150, borderRadius: 16, padding: '1.25rem 1.5rem',
        background: theme === 'light' ? 'linear-gradient(145deg,#ffffff,#f5f5f5)' : 'linear-gradient(145deg,#2a2a2a,#1a1a1a)',
        boxShadow: theme === 'light' ? '0 4px 6px rgba(0,0,0,0.05)' : '0 4px 6px rgba(0,0,0,0.25)',
        ...(fullBorder ? { border: `2px solid ${color}` } : { borderLeft: `4px solid ${color}` }),
      }}
    >
      <div className="text-muted" style={{ fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
      <div style={{ fontSize: '2.6rem', fontWeight: 700, color }}>{Number(value || 0).toLocaleString()}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Door counter card — shows in/out + % of total
// ---------------------------------------------------------------------------
function DoorCounterCard({ door, totalEntrances, totalExits, theme, onClick }) {
  const inPct = totalEntrances ? (door.entrances / totalEntrances) * 100 : 0;
  const outPct = totalExits ? (door.exits / totalExits) * 100 : 0;

  const Bar = ({ pct, color }) => (
    <div style={{ height: 6, borderRadius: 4, background: theme === 'light' ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.12)', overflow: 'hidden' }}>
      <div style={{ width: `${Math.min(100, pct)}%`, height: '100%', background: color, transition: 'width 0.3s ease' }} />
    </div>
  );

  return (
    <div
      role="button"
      onClick={onClick}
      className="panel-2"
      style={{
        cursor: 'pointer', borderRadius: 16, padding: '1.25rem',
        background: theme === 'light' ? 'linear-gradient(145deg,#ffffff,#f5f5f5)' : 'linear-gradient(145deg,#2a2a2a,#1a1a1a)',
        boxShadow: theme === 'light' ? '0 4px 6px rgba(0,0,0,0.05),0 1px 3px rgba(0,0,0,0.1)' : '0 4px 6px rgba(0,0,0,0.2),0 1px 3px rgba(0,0,0,0.3)',
        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
      }}
      onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; }}
      onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; }}
    >
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div className="h5 mb-0" style={{ fontWeight: 700 }}>{door.label}</div>
        <span className="text-muted" style={{ fontSize: '0.75rem' }}>ID: {door.id}</span>
      </div>

      <div className="mb-3">
        <div className="d-flex justify-content-between" style={{ fontSize: '0.8rem' }}>
          <span style={{ color: ENTRANCE_COLOR, fontWeight: 600 }}>Entrances</span>
          <span className="text-muted">{inPct.toFixed(1)}% of total</span>
        </div>
        <div className="d-flex justify-content-between align-items-end mb-1">
          <span style={{ fontSize: '2rem', fontWeight: 700, color: ENTRANCE_COLOR }}>{door.entrances.toLocaleString()}</span>
        </div>
        <Bar pct={inPct} color={ENTRANCE_COLOR} />
      </div>

      <div>
        <div className="d-flex justify-content-between" style={{ fontSize: '0.8rem' }}>
          <span style={{ color: EXIT_COLOR, fontWeight: 600 }}>Exits</span>
          <span className="text-muted">{outPct.toFixed(1)}% of total</span>
        </div>
        <div className="d-flex justify-content-between align-items-end mb-1">
          <span style={{ fontSize: '2rem', fontWeight: 700, color: EXIT_COLOR }}>{door.exits.toLocaleString()}</span>
        </div>
        <Bar pct={outPct} color={EXIT_COLOR} />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Per-door detail modal. Renders `chart` if provided (overview bar chart),
// otherwise falls back to the daily FlowChart on door.series.
// ---------------------------------------------------------------------------
function DoorDetailModal({ door, totalEntrances, totalExits, theme, onClose, chart, subtitle }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  if (!door) return null;
  const inPct = totalEntrances ? (door.entrances / totalEntrances) * 100 : 0;
  const outPct = totalExits ? (door.exits / totalExits) * 100 : 0;

  return (
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, zIndex: 2000, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: 820, borderRadius: 20, padding: '1.75rem',
          background: theme === 'light' ? '#ffffff' : '#1f1f1f',
          color: theme === 'light' ? '#1a1a1a' : '#f0f0f0',
          boxShadow: '0 20px 50px rgba(0,0,0,0.35)', maxHeight: '90vh', overflowY: 'auto',
        }}
      >
        <div className="d-flex justify-content-between align-items-start mb-3">
          <div>
            <div className="h4 mb-0" style={{ fontWeight: 700 }}>{door.label}</div>
            <span className="text-muted" style={{ fontSize: '0.85rem' }}>{subtitle || `Door counter #${door.id}`}</span>
          </div>
          <button onClick={onClose} className="btn" style={{ fontSize: '1.5rem', lineHeight: 1, border: 'none', background: 'transparent', color: 'inherit' }}>&times;</button>
        </div>

        <div className="d-flex gap-3 mb-3 flex-wrap">
          <StatTile label="Entrances" value={door.entrances} color={ENTRANCE_COLOR} theme={theme} fullBorder />
          <StatTile label="Exits" value={door.exits} color={EXIT_COLOR} theme={theme} fullBorder />
          <div className="panel-2" style={{ flex: '1 1 160px', minWidth: 150, borderRadius: 16, padding: '1.25rem 1.5rem', background: theme === 'light' ? 'linear-gradient(145deg,#ffffff,#f5f5f5)' : 'linear-gradient(145deg,#2a2a2a,#1a1a1a)' }}>
            <div className="text-muted" style={{ fontSize: '0.8rem', textTransform: 'uppercase' }}>Share of traffic</div>
            <div style={{ fontSize: '1.1rem', marginTop: 8 }}>
              <div style={{ color: ENTRANCE_COLOR }}>{inPct.toFixed(1)}% in</div>
              <div style={{ color: EXIT_COLOR }}>{outPct.toFixed(1)}% out</div>
            </div>
          </div>
        </div>

        {chart || <FlowChart series={door.series} theme={theme} height={320} />}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Overview range preset tabs
// ---------------------------------------------------------------------------
const PRESETS = [
  { key: 'week', label: 'Week' },
  { key: 'month', label: 'Month' },
  { key: 'ytd', label: 'YTD' },
  { key: 'all', label: 'All-Time' },
];

function PresetTabs({ preset, setPreset, theme }) {
  return (
    <div className="d-flex gap-2 mb-4 flex-wrap">
      {PRESETS.map(p => {
        const active = preset === p.key;
        return (
          <button
            key={p.key}
            onClick={() => setPreset(p.key)}
            className="panel-2"
            style={{
              border: active ? `2px solid ${ENTRANCE_COLOR}` : '1px solid transparent',
              borderRadius: 12, padding: '0.5rem 1.1rem', cursor: 'pointer', fontWeight: 600,
              color: active ? ENTRANCE_COLOR : (theme === 'light' ? '#333' : '#e5e5e5'),
              background: theme === 'light' ? '#ffffff' : '#2a2a2a',
            }}
          >
            {p.label}
          </button>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main
// ---------------------------------------------------------------------------
export default function OfficeLocation() {
  const location = useLocation();
  const [theme] = useContext(ThemeContext);
  const locationData = location.state?.currentLocation;
  const locationId = location.state?.id;
  const [selectedDoor, setSelectedDoor] = useState(null);
  const [preset, setPreset] = useState('all'); // week | month | ytd | all

  return (
    <OfficeLocationController locationData={locationData} locationId={locationId}>
      {({
        loading, regularDayCount, regularDayCountOut, doorData, sensorData,
        isToday, authCtx, refreshData,
        selectedDate, setSelectedDate, minDate, maxDate, enabledDates,
        showCalendar, setShowCalendar,
        viewMode, setViewMode, overviewData, overviewLoading,
      }) => {
        const info = locationData?.doorCounterSensorInfo || {};
        const ids = locationData?.doorCounterSensorIds || [];
        const isOverview = viewMode === 'overview';

        // ----- regular (daily) derived data -----
        const dailyDoorCounters = ids.map(id => {
          const series = (sensorData && sensorData[String(id)]) || [];
          const last = series.length ? series[series.length - 1] : { count: 0, countOut: 0 };
          return {
            id,
            label: info[String(id)]?.label || `Door ${id}`,
            entrances: last.count || 0,
            exits: last.countOut || 0,
            series,
          };
        });
        const dailyTotalIn = regularDayCount || 0;
        const dailyTotalOut = regularDayCountOut || 0;
        const dailyInside = Math.max(0, dailyTotalIn - dailyTotalOut);
        const dailyActive = dailyDoorCounters.filter(d => d.series.length > 0).length;

        // ----- overview derived data (only when we have a payload) -----
        let ov = null;
        if (isOverview && overviewData?.dailySeries) {
          const range = getRange(preset, overviewData.endDate, overviewData.startDate);
          const { buckets, totals } = rollupSeries(overviewData.dailySeries, range);
          const doorCounters = ids.map(id => {
            const daily = (overviewData.perDoorDaily && overviewData.perDoorDaily[String(id)]) || [];
            const rolled = rollupSeries(daily, range);
            return {
              id,
              label: info[String(id)]?.label || `Door ${id}`,
              entrances: rolled.totals.entrances,
              exits: rolled.totals.exits,
              buckets: rolled.buckets,
            };
          });
          ov = {
            range, buckets, totals, doorCounters,
            inside: Math.max(0, totals.entrances - totals.exits),
            active: doorCounters.filter(d => d.entrances || d.exits).length,
          };
        }

        if (!authCtx.loggedIn) {
          return <div className="p-4"><Link to="/auth">Not Authorized. Please log in or contact support.</Link></div>;
        }

        // Spinner: daily loading in regular mode, overview loading in overview mode
        if ((!isOverview && loading) || (isOverview && overviewLoading)) {
          return (
            <div style={{ position: 'fixed', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Spinner size="large" />
            </div>
          );
        }

        return (
          <>
            <div className="sticky-top pt-3" style={{ background: theme === 'light' ? '#ffffff' : '#141414' }}>
              <div className="px-3">
                <OfficeHeaderNav
                  locationData={locationData} loading={loading} isToday={isToday}
                  onRefresh={refreshData} selectedDate={selectedDate} setSelectedDate={setSelectedDate}
                  minDate={minDate} maxDate={maxDate} enabledDates={enabledDates}
                  showCalendar={showCalendar} setShowCalendar={setShowCalendar}
                  viewMode={viewMode} setViewMode={setViewMode}
                />
              </div>
              <hr />
            </div>

            {isOverview ? (
              // ===================== OVERVIEW =====================
              <div className="container-fluid" style={{ padding: 20 }}>
                {!overviewData?.dailySeries ? (
                  <div style={{ padding: 40, textAlign: 'center', color: '#888' }}>
                    No overview data available.
                  </div>
                ) : (
                  <>
                    <PresetTabs preset={preset} setPreset={setPreset} theme={theme} />

                    <div className="d-flex gap-3 mb-4 flex-wrap">
                      <StatTile label="Total Entrances" value={ov.totals.entrances} color={ENTRANCE_COLOR} theme={theme} fullBorder />
                      <StatTile label="Total Exits" value={ov.totals.exits} color={EXIT_COLOR} theme={theme} fullBorder />
                      <StatTile label="Net (in-out)" value={ov.inside} color="#10b981" theme={theme} fullBorder />
                      <StatTile label="Active Doors" value={ov.active} color="#8b5cf6" theme={theme} fullBorder />
                    </div>

                    <div
                      className="panel-2 mb-4"
                      style={{
                        borderRadius: 24, padding: '1.25rem 1.5rem',
                        background: theme === 'light' ? '#ffffff' : '#1c1c1c',
                        boxShadow: theme === 'light' ? '0 4px 8px rgba(0,0,0,0.1)' : '0 4px 8px rgba(0,0,0,0.3)',
                      }}
                    >
                      <p style={{ fontSize: 22, margin: '0 0 8px', fontWeight: 600 }}>Traffic by Period</p>
                      <OverviewBarChart buckets={ov.buckets} theme={theme} height={320} />
                    </div>

                    <div style={{ fontSize: '1.4rem', fontWeight: 600, margin: '8px 0 16px' }}>Door Counters</div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 24 }}>
                      {ov.doorCounters.map(door => (
                        <DoorCounterCard
                          key={door.id} door={door}
                          totalEntrances={ov.totals.entrances} totalExits={ov.totals.exits}
                          theme={theme} onClick={() => setSelectedDoor(door)}
                        />
                      ))}
                    </div>
                  </>
                )}
              </div>
            ) : (
              // ===================== REGULAR DAY =====================
              <div className="container-fluid" style={{ padding: 20 }}>
                <div className="d-flex gap-3 mb-4 flex-wrap">
                  <StatTile label="Total Entrances" value={dailyTotalIn} color={ENTRANCE_COLOR} theme={theme} fullBorder />
                  <StatTile label="Total Exits" value={dailyTotalOut} color={EXIT_COLOR} theme={theme} fullBorder />
                  <StatTile label="Inside (net)" value={dailyInside} color="#10b981" theme={theme} fullBorder />
                  <StatTile label="Active Entrances" value={dailyActive} color="#8b5cf6" theme={theme} fullBorder />
                </div>

                <div
                  className="panel-2 mb-4"
                  style={{
                    borderRadius: 24, padding: '1.25rem 1.5rem',
                    background: theme === 'light' ? '#ffffff' : '#1c1c1c',
                    boxShadow: theme === 'light' ? '0 4px 8px rgba(0,0,0,0.1)' : '0 4px 8px rgba(0,0,0,0.3)',
                  }}
                >
                  <p style={{ fontSize: 22, margin: '0 0 8px', fontWeight: 600 }}>Traffic Flow Over Time</p>
                  <FlowChart series={doorData} theme={theme} height={320} />
                </div>

                <div style={{ fontSize: '1.4rem', fontWeight: 600, margin: '8px 0 16px' }}>Door Counters</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 24 }}>
                  {dailyDoorCounters.map(door => (
                    <DoorCounterCard
                      key={door.id} door={door}
                      totalEntrances={dailyTotalIn} totalExits={dailyTotalOut}
                      theme={theme} onClick={() => setSelectedDoor(door)}
                    />
                  ))}
                </div>
              </div>
            )}

            {selectedDoor && (
              <DoorDetailModal
                door={selectedDoor}
                totalEntrances={isOverview ? (ov?.totals.entrances || 0) : dailyTotalIn}
                totalExits={isOverview ? (ov?.totals.exits || 0) : dailyTotalOut}
                theme={theme}
                onClose={() => setSelectedDoor(null)}
                chart={isOverview ? <OverviewBarChart buckets={selectedDoor.buckets} theme={theme} height={320} /> : undefined}
                subtitle={isOverview ? `Door #${selectedDoor.id} · selected range` : undefined}
              />
            )}
          </>
        );
      }}
    </OfficeLocationController>
  );
}